import { ReadinessModel, RouteClass } from "../contracts/common.js";
import { failContract } from "../internal/errors.js";
import { expectNumber, expectObject, expectOptionalNumber, expectString, readField, readOptionalField, } from "../internal/json.js";
import { encodePathSegment } from "../internal/query.js";
import { requestJsonResult } from "../internal/transport.js";
/**
 * Source class for one raw payload response.
 */
export var RawValueSource;
(function (RawValueSource) {
    RawValueSource["INDEXED"] = "indexed";
    RawValueSource["LIVE"] = "live";
})(RawValueSource || (RawValueSource = {}));
const STATUS_ROUTE = {
    method: "GET",
    name: "status",
    path: "/api/v1/status",
    readinessModel: ReadinessModel.NONE,
    routeClass: RouteClass.CROSS_DOMAIN,
};
const RAW_BLOCK_ROUTE = {
    method: "GET",
    name: "rawBlock",
    path: "/api/v1/rawblock/{hashOrHeight}",
    readinessModel: ReadinessModel.INDEXED,
    routeClass: RouteClass.RAW,
};
const RAW_TX_ROUTE = {
    method: "GET",
    name: "rawTx",
    path: "/api/v1/rawtx/{txid}",
    readinessModel: ReadinessModel.NONE,
    routeClass: RouteClass.RAW,
};
/**
 * Public route descriptors for status and raw payload routes.
 */
export const statusRawRouteDescriptors = {
    rawBlock: RAW_BLOCK_ROUTE,
    rawTx: RAW_TX_ROUTE,
    status: STATUS_ROUTE,
};
function parseStageStatus(value, path) {
    const object = expectObject(value, path);
    return {
        indexedHeight: expectNumber(readField(object, "indexedHeight", path), `${path}.indexedHeight`),
        updatedAt: expectString(readField(object, "updatedAt", path), `${path}.updatedAt`),
    };
}
function parseStatusResponse(value, path) {
    const object = expectObject(value, path);
    const rawIndices = expectObject(readField(object, "indices", path), `${path}.indices`);
    const indices = Object.fromEntries(Object.entries(rawIndices).map(([stage, status]) => [stage, parseStageStatus(status, `${path}.indices.${stage}`)]));
    return {
        syncStatus: expectString(readField(object, "syncStatus", path), `${path}.syncStatus`),
        blockHeight: expectNumber(readField(object, "blockHeight", path), `${path}.blockHeight`),
        indices,
        version: expectString(readField(object, "version", path), `${path}.version`),
    };
}
function parseRawValueSource(value, path) {
    const source = expectString(value, path);
    switch (source) {
        case "indexed":
            return RawValueSource.INDEXED;
        case "live":
            return RawValueSource.LIVE;
    }
    return failContract(path, `unsupported raw value source ${source}`);
}
function parseRawBlockResponse(value, path) {
    const object = expectObject(value, path);
    return {
        asOfHeight: expectNumber(readField(object, "asOfHeight", path), `${path}.asOfHeight`),
        source: parseRawValueSource(readField(object, "source", path), `${path}.source`),
        rawblock: expectString(readField(object, "rawblock", path), `${path}.rawblock`),
    };
}
function parseRawTxResponse(value, path) {
    const object = expectObject(value, path);
    const asOfHeight = expectOptionalNumber(readOptionalField(object, "asOfHeight"), `${path}.asOfHeight`);
    return {
        ...(asOfHeight == null ? {} : { asOfHeight }),
        source: parseRawValueSource(readField(object, "source", path), `${path}.source`),
        rawtx: expectString(readField(object, "rawtx", path), `${path}.rawtx`),
    };
}
export function buildStatusRoutes(context) {
    return {
        async getStatus() {
            return requestJsonResult(context, STATUS_ROUTE, STATUS_ROUTE.path, parseStatusResponse);
        },
    };
}
export function buildRawRoutes(context) {
    return {
        async getBlock(hashOrHeight) {
            return requestJsonResult(context, RAW_BLOCK_ROUTE, `/api/v1/rawblock/${encodePathSegment(String(hashOrHeight))}`, parseRawBlockResponse);
        },
        async getTx(txid) {
            return requestJsonResult(context, RAW_TX_ROUTE, `/api/v1/rawtx/${encodePathSegment(txid)}`, parseRawTxResponse);
        },
    };
}
//# sourceMappingURL=statusRaw.js.map